import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Toggle } from 'framework7-react';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { uniq, uniqBy } from 'lodash';
import pluralize from 'pluralize';

import API from '@api/API';

import { useAppSettingsQuery } from '@queries/AppQueries';
import { useCurrentEntertainerQuery, useEntertainerDirectory } from '@queries/EntertainerQueries';
import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';
import {
  Series,
  useSeriesByTagId,
  useSeriesByTagSlug,
  useSeriesEntertainersQuery,
} from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { CurrentCity, useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';

import {
  EntertainerDashboardAction,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
} from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { SeriesEntertainersList } from '@screens/series/SeriesScreen/SeriesScreen';
import {
  SweepstakesCard,
  useSweepstakesReferralLink,
} from '@screens/series/SeriesScreen/SweepstakesCard';

import { CitySelector } from '../DragKingCommunityScreen/DragKingThingShoutoutsScreen';
import { KingOfDragBanner } from '../DragKingCommunityScreen/KingOfDragBanner';
import { ScheduledSocialPost } from '../DragKingCommunityScreen/ScheduledSocialPost';
import { TopKingMaterialSection } from '../DragKingCommunityScreen/TopKingMaterialSection';
import { useCityDragKingSeriesEntertainers } from '../DragKingCommunityScreen/useCityDragKingSeriesEntertainers';
import { useCityDragKingSeriesEvents } from '../DragKingCommunityScreen/useCityDragKingSeriesEvents';
import { useCityDragKingSeriesMostBookedEntertainers } from '../DragKingCommunityScreen/useCityDragKingSeriesMostBookedEntertainers';
import { FeaturedKing } from '../EntertainerDetailsScreen/assets/FeaturedKing';
import { LandingPageSweepstakesCard } from '../EntertainerSweepstakesScreen/EntertainerSweepstakesScreen';
import { FeaturedLook, useFeaturedLooksQuery } from '../FeaturedLooksScreen/FeaturedLooksScreen';
import {
  AddFeaturedPhotoPopup,
  AddImageContent,
} from '../components/FeaturedPhotoManagementSection';
import tickets from './tickets.svg';

interface DragKingCommunityScreenProps {}

interface DragKingCommunitySubmission {
  _id: string;
  title: string;
  image?: string;
  status: string;
  entertainer?: {
    _id: string;
    title: string;
    bookingHandle: string;
  };
}

const useDragKingCommunitySubmissions = (isEnabled: boolean) => {
  const { data, isFetched } = useQuery({
    queryKey: ['dragKingCommunitySubmissions'],
    queryFn: () =>
      API.get<{ submissions: DragKingCommunitySubmission[] }>(
        `/v1/drag-king-community/submissions`,
      ),
    enabled: isEnabled,
  });

  return {
    submissions: data?.submissions,
    areSubmissionsReady: isFetched,
  };
};

const useUpdateDragKingCommunitySubmission = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      entertainerMediaId,
      status,
      temp,
    }: {
      entertainerMediaId: string;
      status: 'approved' | 'rejected';
      temp?: {
        entertainerInstagram?: string;
        base64Image?: any;
        path?: string;
        title?: string;
        addCollaborator?: boolean;
      };
    }) =>
      API.post(`/v1/drag-king-community/submissions`, {
        entertainerMediaId,
        status,
        temp,
      }),
  });

  return {
    isUpdatingSubmission: isPending,
    updateSubmissionAsync: mutateAsync,
  };
};

const DragKingSubmissionCard: FC<{
  submission: DragKingCommunitySubmission;
}> = ({ submission }) => {
  const [status, setStatus] = useState(submission.status);
  const [title, setTitle] = useState(submission.entertainer?.title);
  const { updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleApprove = async () => {
    setStatus('approved');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'approved',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  const handleReject = async () => {
    setStatus('rejected');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'rejected',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  return (
    <div className="d-grid gap-2">
      <img
        src={submission.image}
        alt={submission.title}
        className="w-100"
        style={{ borderRadius: 8 }}
      />
      <Input
        defaultValue={submission.entertainer.title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="d-flex gap-2">
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'approved' ? 'default' : 'outlined'}
          onClick={handleApprove}
        >
          Approve
        </Button>
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'rejected' ? 'default' : 'outlined'}
          onClick={handleReject}
        >
          Reject
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopupContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [entertainerInstagram, setInstagram] = useState('');
  const [base64Image, setUpdatedImage] = useState<string | ArrayBuffer>();
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('approved');
  const [addCollaborator, setAddCollaborator] = useState(true);

  const { isUpdatingSubmission, updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleSubmit = async () => {
    await updateSubmissionAsync({
      entertainerMediaId: '',
      status: status as any,
      temp: {
        entertainerInstagram,
        base64Image,
        title,
        addCollaborator,
      },
    });

    onClose();
  };

  return (
    <div className="d-grid gap-2">
      <Select
        options={[
          { value: 'approved', label: 'Approved' },
          { value: 'pending', label: 'Pending' },
        ]}
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      />
      <Input
        placeholder="Stage Name *"
        name="stageName"
        onChange={(event) => setTitle(event.target.value)}
      />
      <Input
        placeholder="Instagram *"
        name="instagram"
        onChange={(event) => setInstagram(event.target.value)}
      />
      <div className="d-flex gap-2 align-items-center">
        <Toggle defaultChecked={addCollaborator} onToggleChange={setAddCollaborator} />
        <div>
          <strong>Add Collaborator</strong>
        </div>
      </div>
      <Card>
        <ImageUploadField
          name="customSubmissionImage"
          placeholder="Select an image"
          onChange={(blob) => setUpdatedImage(blob)}
        />
      </Card>
      <div>
        <Button
          roundness="rounded"
          disabled={!base64Image || !title || isUpdatingSubmission}
          onClick={handleSubmit}
        >
          {isUpdatingSubmission ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopup: FC<{ isVisible: boolean; onClose: () => void }> = ({
  isVisible,
  onClose,
}) => {
  return (
    <Popup title="Add Custom" isVisible={isVisible} onClose={onClose} height="full">
      <Popup.Content>
        {isVisible && <AddCustomDragKingSubmissionPopupContent onClose={onClose} />}
      </Popup.Content>
    </Popup>
  );
};

const useShareEntertainerMediaPostMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ entertainerMediaId, title }: { entertainerMediaId: string; title?: string }) =>
      API.post(`/v1/entertainer-media/${entertainerMediaId}/share-badges-post`, {
        title,
      }),
  });

  return {
    isSharing: isPending,
    shareEntertainerMediaPostAsync: mutateAsync,
  };
};

const BadgeCountCard: FC<{ featuredLook: FeaturedLook }> = ({ featuredLook }) => {
  const rootImageUrl = `https://gaggl.app/api/v1/entertainer-media/${featuredLook._id}/badges-post.jpg`;
  const [title, setTitle] = useState<string>();
  const [imageUrl, setImageUrl] = useState(rootImageUrl);
  const [isShared, setIsShared] = useState(false);

  const { shareEntertainerMediaPostAsync } = useShareEntertainerMediaPostMutation();

  const handleShare = async () => {
    if (!isShared) {
      setIsShared(true);
      await shareEntertainerMediaPostAsync({
        entertainerMediaId: featuredLook._id,
        title,
      });
    }
  };

  return (
    <div className="d-grid gap-2">
      <img src={imageUrl} alt={featuredLook.title} className="w-100" style={{ borderRadius: 8 }} />
      <Input defaultValue={featuredLook.title} onChange={(e) => setTitle(e.target.value)} />
      <div className="d-flex gap-2">
        <Button
          roundness="rounded"
          color="neutral"
          variant="outlined"
          onClick={() => {
            setImageUrl(`${rootImageUrl}?title=${encodeURIComponent(title)}`);
          }}
        >
          Update Title
        </Button>
        <Button roundness="rounded" variant="outlined" color="neutral" onClick={handleShare}>
          {isShared ? 'Shared' : 'Share Image'}
        </Button>
      </div>
    </div>
  );
};

const ShareBadgeCountContent = () => {
  const { featuredLooksAreReady, featuredLooks = [] } = useFeaturedLooksQuery({});

  const [end, setEnd] = useState(5);

  const kingsThingsLooks = useMemo(
    () => featuredLooks.filter(({ entertainer }) => entertainer.isDragKingThing),
    [featuredLooks],
  );

  return (
    <>
      <TitleToolbar text="Latest Badges Shared" size="sm" />

      <div className="d-grid gap-4">
        {kingsThingsLooks.slice(0, Math.min(end, kingsThingsLooks.length)).map((featuredLook) => (
          <BadgeCountCard key={featuredLook._id} featuredLook={featuredLook} />
        ))}
        <Button
          roundness="rounded"
          variant="smooth"
          color="neutral"
          onClick={() => setEnd((prevValue) => prevValue + 5)}
        >
          Load More
        </Button>
      </div>
    </>
  );
};

const SHARE_OPTIONS = [
  { value: 'join', label: 'Entertainers' },
  { value: 'look', label: 'Add Look' },
  // { value: 'badge', label: 'Reward Badges' },
  { value: 'show', label: 'Add Show' },
];

const MostBookedCard: FC<{ series: Series; back: string }> = ({ series, back }) => {
  const { navigate } = useRouter();
  const handleClick = () => {
    navigate(`/noauth/series/${series?._id}/shows`, {
      state: { back },
    });
  };

  const { eventsAreReady, events = [] } = useUnauthorizedEventsQuery(
    {
      tagIds: [series?._id],
      startDate: dayjs().format('YYYY-MM-DD'),
      rsvpData: 'ignore',
    },
    Boolean(series?._id),
  );
  const castEntertainers = uniqBy(
    events.flatMap(({ entertainers }) => entertainers).filter((entertainer) => entertainer?._id),
    ({ _id }) => _id,
  );

  return (
    <div className="p-3" style={{ background: '#009688bb', borderRadius: 8 }} onClick={handleClick}>
      <div className="fs-5">
        <strong>Booked & Busy Performers</strong>
      </div>
      <div className="mb-2">
        {eventsAreReady
          ? `${events.length} upcoming shows. ${castEntertainers.length} performers cast.`
          : '--'}
      </div>
      <div style={{ fontSize: '.7rem' }}>See details →</div>
    </div>
  );
};

const EntertainerDirectorySections: FC<{ recommendations: any[]; back: string }> = ({
  recommendations,
  back,
}) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();

  const featuredIconSize = 24;

  return (
    <>
      {/* <TitleToolbar text="Local Recommendations" size="sm" /> */}
      <div className="mt-3">
        <div className="d-grid gap-2 mb-3">
          {recommendations?.map((section) => (
            <div
              className="d-flex align-items-center gap-2 p-2"
              style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8 }}
              onClick={() => {
                track('Featured Row Clicked', section);
                navigate(section.to, {
                  state: { back, forceBack: true },
                });
              }}
            >
              <div
                style={{
                  height: featuredIconSize,
                  width: featuredIconSize,
                }}
              >
                <img
                  className="d-block"
                  src={section.imgUrl}
                  alt={section.title}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 2,
                  }}
                />
              </div>
              <div>
                <strong>{section.title}</strong>
              </div>
              <div className="ml-auto px-2 d-flex align-items-center gap-3">
                <div>{section.count ?? 0}</div>
                <FontAwesomeIcon name="chevron-right" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const VIEW_AS_USER = false;

interface CityEntertainerRecommendationGroupsScreenProps {}

export const CityEntertainerRecommendationGroupsScreen =
  createScreenComponent<CityEntertainerRecommendationGroupsScreenProps>(({ f7route }) => {
    const { track } = useAnalytics();
    const navigate = useNavigate();
    const [cityId, setCityId] = useState<string>(f7route.query?.cityId);
    const [didSubmitCity, setDidSubmitCity] = useState(!!f7route.query?.cityId);
    const [isInCitySelectorMode, setIsInCitySelectorMode] = useState(false);
    const { currentUser, refetchUser } = useCurrentUser();
    const { entertainer } = useCurrentEntertainerQuery();
    const { city } = useCityById(cityId ?? entertainer?.cityIds?.[0]);
    const { submissions = [] } = useDragKingCommunitySubmissions(!!entertainer?._id);
    const { createAuthGateHandler } = useAuthGate();
    const [addImageModeIsVisible, setAddImageModeIsVisible] = useState(false);
    const [addCustomSubmissionModeIsVisible, setAddCustomSubmissionModeIsVisible] = useState(false);
    const [looksOrBadges, setLooksOrBadges] = useState('default');

    const {
      isFetched: isSeriesReady,
      series,
      recommendations,
    } = useSeriesByTagId(city?.featuredSeries?.drag);

    const hasDragSeries = Boolean(series?.isEntertainerSeries);
    const {
      isFetched: seriesEntertainersAreReady,
      entertainers: seriesEntertainers = [],
      refetch: refetchSeriesEntertainers,
    } = useSeriesEntertainersQuery(city?.featuredSeries?.drag, hasDragSeries);

    const currentPath = `/noauth/entertainer-recommendations?cityId=${city?._id}`;

    return (
      <Screen
        name="City Entertainer Recommendation Groups Screen"
        hideToolbar
        headerProps={{
          title: <Screen.GagglTitle />,
          right: <ShareLinkButton url={`https://gaggl.app/drag-shoutouts`} />,
        }}
      >
        <Screen.Content>
          <div className="mb-3">
            <KingOfDragBanner context="City Entertainer Recommendation Groups Screen" />
          </div>

          <div className="d-flex mb-3 gap-3 align-items-center">
            <FeaturedKing height={48} width={48} />
            <div>
              <strong className="fs-5">Drag Recommendations</strong>
              <br />
              <span>Help recommend local performers 👑</span>
            </div>
          </div>

          <p className="mt-0">
            <strong>Gaggl</strong> is here to celebrate the drag artists who don't always get the
            spotlight — kings, things, and everything in between. Recommend artists across multiple
            categories to help folks get more visibility in the community.
          </p>

          <TopKingMaterialSection context="City Entertainer Recommendation Groups Screen" />

          {!isInCitySelectorMode && (didSubmitCity || entertainer?._id) ? (
            <>
              <div className="d-flex gap-2 align-items-center justify-content-between mt-4 mb-2">
                <div className="fs-6">
                  <strong>{city?.name}</strong>
                </div>
                <div>
                  <Button
                    className="py-1 px-3"
                    size="sm"
                    roundness="rounded"
                    variant="smooth"
                    color="neutral"
                    onClick={() => {
                      setIsInCitySelectorMode(true);
                    }}
                  >
                    Change City
                  </Button>
                </div>
              </div>

              {hasDragSeries && (
                <>
                  {isSeriesReady ? (
                    <>
                      <MostBookedCard series={series} back={currentPath} />

                      <EntertainerDirectorySections
                        recommendations={recommendations?.sections}
                        back={currentPath}
                      />

                      <TitleToolbar
                        size="sm"
                        text="Local Directory"
                        action={<span>{`${seriesEntertainers.length} total`}</span>}
                      />
                      <SeriesEntertainersList
                        series={series}
                        city={city}
                        back={currentPath}
                        joinCategories={[]}
                        joinCallback={() => {}}
                      />
                    </>
                  ) : (
                    <>
                      <Skeleton height={40} />
                      <Skeleton height={40} />
                      <Skeleton height={40} />
                      <Skeleton height={40} />
                      <Skeleton height={40} />
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <CitySelector
              isInCitySelectorMode={isInCitySelectorMode}
              onboardingCategories={[]}
              cityId={cityId}
              onChangeCityId={setCityId}
              onContinue={() => {
                track('Select City', {
                  cityName: city?.name,
                });
                setDidSubmitCity(true);
                setIsInCitySelectorMode(false);
                setLooksOrBadges('default');
              }}
              onIsProcessingCity={() => {
                setDidSubmitCity(true);
              }}
            />
          )}
        </Screen.Content>
        <AddCustomDragKingSubmissionPopup
          isVisible={addCustomSubmissionModeIsVisible}
          onClose={() => setAddCustomSubmissionModeIsVisible(false)}
        />
      </Screen>
    );
  });
